/**
 * Search utilities for predictive service search
 * Maps keywords to services for intelligent search suggestions
 */

// Comprehensive keyword mapping for services
const SERVICE_KEYWORDS = {
  'plumbing': {
    keywords: [
      // Water issues
      'water leak', 'water leaking', 'leaky', 'leak', 'leaks', 'dripping', 'drip',
      'water damage', 'flood', 'overflow', 'burst pipe', 'pipe burst', 'broken pipe',
      'water pressure', 'low water pressure', 'high water pressure', 'water pressure low',
      
      // Faucets and taps
      'faucet', 'tap', 'tap leak', 'leaky faucet', 'leaky tap', 'dripping tap', 'dripping faucet',
      'broken faucet', 'broken tap', 'faucet not working', 'tap not working',
      'hot water', 'cold water', 'no hot water', 'no cold water',
      
      // Drains and pipes
      'drain', 'drains', 'drain clogged', 'clogged drain', 'blocked drain', 'slow drain',
      'drain blocked', 'drainage', 'pipe', 'pipes', 'pipe leak', 'pipe broken',
      'toilet clogged', 'toilet blocked', 'sink clogged', 'sink blocked',
      'shower drain', 'bath drain', 'kitchen sink', 'bathroom sink',
      
      // Plumbing fixtures
      'toilet', 'toilet not working', 'toilet running', 'toilet leak', 'toilet repair',
      'shower', 'shower leak', 'shower head', 'bath', 'bathtub', 'bathtub leak',
      'water heater', 'geyser', 'geyser not working', 'hot water heater',
      
      // Emergency terms
      'plumbing emergency', 'water emergency', 'urgent plumbing', 'emergency plumber'
    ],
    serviceName: 'Plumbing',
    serviceId: 'plumbing'
  },
  
  'electrical': {
    keywords: [
      // Power issues
      'no power', 'power cut', 'electricity not working', 'power outage', 'blackout',
      'circuit breaker', 'fuse', 'fuse blown', 'tripped breaker', 'breaker tripped',
      'electrical problem', 'electrical issue', 'power problem',
      
      // Outlets and switches
      'outlet', 'outlets', 'socket', 'sockets', 'plug', 'plugs', 'outlet not working',
      'socket not working', 'plug not working', 'no power outlet', 'dead outlet',
      'switch', 'switches', 'light switch', 'switch not working', 'broken switch',
      
      // Lighting
      'light', 'lights', 'lighting', 'light not working', 'lights not working',
      'flickering light', 'light flickering', 'dim light', 'bright light',
      'ceiling light', 'wall light', 'lamp', 'bulb', 'bulbs', 'led light',
      
      // Wiring and installation
      'wiring', 'wire', 'wires', 'cable', 'cables', 'electrical wiring',
      'new outlet', 'install outlet', 'new switch', 'install switch',
      'ceiling fan', 'fan', 'fan not working', 'fan installation',
      
      // Safety issues
      'electrical shock', 'sparking', 'electric spark', 'burning smell',
      'electrical fire', 'smoke', 'electrical safety', 'urgent electrical'
    ],
    serviceName: 'Electrical',
    serviceId: 'electrical'
  },
  
  'ac-repair': {
    keywords: [
      // Cooling issues
      'ac not working', 'air conditioning not working', 'ac not cooling', 'not cooling',
      'ac not cold', 'not cold', 'ac warm', 'warm air', 'hot air', 'no cold air',
      'ac blowing hot', 'hot air coming', 'ac not blowing cold',
      
      // Temperature problems
      'ac too hot', 'room too hot', 'hot room', 'temperature', 'thermostat',
      'ac not reaching temperature', 'temperature not dropping', 'room not cooling',
      
      // Air flow issues
      'ac not blowing', 'no air flow', 'weak air flow', 'low air flow', 'air not coming',
      'ac fan not working', 'fan not spinning', 'air circulation', 'ventilation',
      
      // AC maintenance
      'ac service', 'ac maintenance', 'ac cleaning', 'ac repair', 'ac installation',
      'ac check', 'ac inspection', 'ac tune up', 'ac filter', 'filter change',
      
      // AC parts
      'compressor', 'condenser', 'evaporator', 'refrigerant', 'freon', 'gas',
      'ac unit', 'air conditioner', 'split ac', 'window ac', 'central ac',
      
      // Heating
      'heating', 'heater', 'heater not working', 'no heat', 'heat not working',
      'furnace', 'furnace not working', 'hvac', 'hvac not working'
    ],
    serviceName: 'AC Repair',
    serviceId: 'ac-repair'
  },
  
  'deep-cleaning': {
    keywords: [
      // General cleaning
      'cleaning', 'clean', 'house cleaning', 'home cleaning', 'deep cleaning',
      'thorough cleaning', 'complete cleaning', 'full cleaning', 'spring cleaning',
      
      // Room specific
      'kitchen cleaning', 'bathroom cleaning', 'bedroom cleaning', 'living room cleaning',
      'office cleaning', 'basement cleaning', 'garage cleaning', 'attic cleaning',
      
      // Specific cleaning needs
      'carpet cleaning', 'rug cleaning', 'floor cleaning', 'tile cleaning',
      'window cleaning', 'window washing', 'mirror cleaning', 'glass cleaning',
      
      // Deep cleaning tasks
      'move in cleaning', 'move out cleaning', 'post construction cleaning',
      'after party cleaning', 'holiday cleaning', 'vacation rental cleaning',
      
      // Specialized cleaning
      'mold removal', 'mold cleaning', 'stain removal', 'odor removal',
      'pet hair removal', 'allergen removal', 'sanitization', 'disinfection',
      
      // Cleaning services
      'maid service', 'housekeeping', 'cleaning service', 'professional cleaning',
      'residential cleaning', 'commercial cleaning'
    ],
    serviceName: 'Deep Cleaning',
    serviceId: 'deep-cleaning'
  },
  
  'moving': {
    keywords: [
      // Moving services
      'moving', 'move', 'relocation', 'relocate', 'moving company', 'movers',
      'packing', 'packing service', 'packing and moving', 'move out', 'move in',
      
      // Transportation
      'transportation', 'shipping', 'delivery', 'pickup', 'truck rental',
      'moving truck', 'moving van', 'cargo', 'freight',
      
      // Packing materials
      'boxes', 'packing boxes', 'packing materials', 'bubble wrap', 'packing tape',
      'packing supplies', 'moving supplies',
      
      // Storage
      'storage', 'storage unit', 'storage facility', 'temporary storage',
      'long term storage', 'short term storage',
      
      // Moving types
      'local move', 'long distance move', 'interstate move', 'international move',
      'office move', 'commercial move', 'residential move'
    ],
    serviceName: 'Moving',
    serviceId: 'moving'
  },
  
  'painting': {
    keywords: [
      // Painting services
      'painting', 'paint', 'painter', 'house painting', 'home painting',
      'interior painting', 'exterior painting', 'wall painting', 'room painting',
      
      // Paint types
      'paint job', 'paint work', 'fresh paint', 'new paint', 'repaint',
      'touch up', 'touch up painting', 'paint correction',
      
      // Surface preparation
      'primer', 'priming', 'surface preparation', 'wall preparation',
      'sanding', 'scraping', 'cleaning walls',
      
      // Painting areas
      'bedroom painting', 'kitchen painting', 'bathroom painting', 'living room painting',
      'exterior walls', 'interior walls', 'ceiling painting', 'door painting',
      'window painting', 'trim painting',
      
      // Color and finish
      'color selection', 'paint color', 'color consultation', 'paint consultation',
      'finish', 'paint finish', 'matte', 'glossy', 'semi gloss',
      
      // Specialized painting
      'textured painting', 'stucco', 'mural', 'decorative painting', 'faux finish'
    ],
    serviceName: 'Painting',
    serviceId: 'painting'
  },
  
  'pest-control': {
    keywords: [
      // Common pests
      'pest', 'pests', 'pest control', 'pest removal', 'pest extermination',
      'bug', 'bugs', 'insect', 'insects', 'insect control', 'bug control',
      
      // Specific pests
      'ant', 'ants', 'cockroach', 'cockroaches', 'roach', 'roaches',
      'spider', 'spiders', 'mosquito', 'mosquitoes', 'fly', 'flies',
      'termite', 'termites', 'bed bug', 'bed bugs', 'rat', 'rats', 'mouse', 'mice',
      
      // Pest problems
      'infestation', 'infested', 'pest problem', 'bug problem', 'insect problem',
      'pest invasion', 'bug invasion', 'pest damage', 'pest prevention',
      
      // Treatment types
      'pest treatment', 'pest spray', 'spraying', 'fumigation', 'fumigate',
      'pest inspection', 'pest assessment', 'pest management',
      
      // Areas
      'kitchen pest', 'bathroom pest', 'bedroom pest', 'basement pest',
      'garden pest', 'outdoor pest', 'indoor pest'
    ],
    serviceName: 'Pest Control',
    serviceId: 'pest-control'
  }
};

/**
 * Find matching services based on search query
 * @param {string} query - The search query
 * @returns {Array} Array of matching services with scores
 */
export const findMatchingServices = (query) => {
  if (!query || query.trim().length < 2) {
    return [];
  }

  const normalizedQuery = query.toLowerCase().trim();
  const matches = [];

  // Check each service's keywords
  Object.entries(SERVICE_KEYWORDS).forEach(([serviceKey, serviceData]) => {
    let score = 0;
    let matchedKeywords = [];

    serviceData.keywords.forEach(keyword => {
      const keywordLower = keyword.toLowerCase();
      
      // Exact match gets highest score
      if (normalizedQuery === keywordLower) {
        score += 100;
        matchedKeywords.push(keyword);
      }
      // Query contains keyword
      else if (normalizedQuery.includes(keywordLower)) {
        score += 50;
        matchedKeywords.push(keyword);
      }
      // Keyword contains query
      else if (keywordLower.includes(normalizedQuery)) {
        score += 25;
        matchedKeywords.push(keyword);
      }
      // Word boundary matches
      else {
        const queryWords = normalizedQuery.split(/\s+/);
        const keywordWords = keywordLower.split(/\s+/);
        
        queryWords.forEach(queryWord => {
          keywordWords.forEach(keywordWord => {
            if (queryWord === keywordWord) {
              score += 10;
              matchedKeywords.push(keyword);
            } else if (queryWord.includes(keywordWord) || keywordWord.includes(queryWord)) {
              score += 5;
              matchedKeywords.push(keyword);
            }
          });
        });
      }
    });

    if (score > 0) {
      matches.push({
        serviceKey,
        serviceName: serviceData.serviceName,
        serviceId: serviceData.serviceId,
        score,
        matchedKeywords: [...new Set(matchedKeywords)] // Remove duplicates
      });
    }
  });

  // Sort by score (highest first) and return top matches
  return matches
    .sort((a, b) => b.score - a.score)
    .slice(0, 5); // Return top 5 matches
};

/**
 * Get search suggestions based on partial query
 * @param {string} query - The partial search query
 * @returns {Array} Array of suggestion strings
 */
export const getSearchSuggestions = (query) => {
  if (!query || query.trim().length < 2) {
    return [];
  }

  const normalizedQuery = query.toLowerCase().trim();
  const suggestions = new Set();

  // Find keywords that start with the query
  Object.values(SERVICE_KEYWORDS).forEach(serviceData => {
    serviceData.keywords.forEach(keyword => {
      const keywordLower = keyword.toLowerCase();
      if (keywordLower.startsWith(normalizedQuery) && keywordLower.length > normalizedQuery.length) {
        suggestions.add(keyword);
      }
    });
  });

  // Convert to array and limit results
  return Array.from(suggestions).slice(0, 8);
};

/**
 * Get popular search terms for suggestions
 * @returns {Array} Array of popular search terms
 */
export const getPopularSearchTerms = () => {
  return [
    'water leak',
    'ac not cooling',
    'electrical problem',
    'deep cleaning',
    'pest control',
    'leaky faucet',
    'no power',
    'moving service'
  ];
};

/**
 * Highlight matching keywords in text
 * @param {string} text - The text to highlight
 * @param {string} query - The search query
 * @returns {string} HTML string with highlighted matches
 */
export const highlightMatches = (text, query) => {
  if (!query || !text) return text;
  
  const regex = new RegExp(`(${query.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')})`, 'gi');
  return text.replace(regex, '<mark>$1</mark>');
};

const searchUtils = {
  findMatchingServices,
  getSearchSuggestions,
  getPopularSearchTerms,
  highlightMatches,
  SERVICE_KEYWORDS
};

export default searchUtils;

